import React, { ReactElement, useCallback, useEffect } from 'react';
import styled from 'styled-components';
import { observer } from 'mobx-react-lite';
import { Trans, useTranslation } from 'react-i18next';
import { colorsPalette } from '../../../../../shared/ui/styles/colors-palette';
import { useUILogic } from '../../../shared/providers/UILogicProvider/hooks';
import { phoneUtils } from '../../../../../shared/infrastructure/utils/phone-utils';
import { PhoneMethodSelector } from '../PhoneMethodSelector';
import { QRConfirmation } from './QRConfirmation';
import { IncomingConfirmation } from './IncomingConfirmation';
import { OutgoingConfirmation } from './OutgoingConfirmation';
import { LoadingStatus } from '../../../../../shared/infrastructure/types/common/common-types';
import UiKit from '../../../../../shared/ui/ui-kit';

interface PhoneConfirmationProps {}

let PhoneConfirmation = function ({}: PhoneConfirmationProps): ReactElement {
  const { t } = useTranslation();
  const { authorizationUiModule, adaptiveUIModule } = useUILogic();
  const isMobile = adaptiveUIModule.isMobile;
  const coreManager = authorizationUiModule.registerUIManager.coreManager;
  const outgoingManager = authorizationUiModule.registerUIManager.coreManager.outgoingManager;

  const method = coreManager.confirmationMethod;

  return (
    <Wrapper>
      <Header>
        <Heading $isMobile={isMobile}>{t('register.phone.confirm.heading')}</Heading>
        {method === 'outgoing' && (
          <Description $isMobile={isMobile}>
            <Trans
              i18nKey={'register.phone.confirm.description'}
              values={{ phone: phoneUtils.formatPhoneString(coreManager.phone as PhoneNumber) }}
              components={{
                br: adaptiveUIModule.isMobile ? <br /> : <></>,
                u: <u />,
                changePhoneBtn: (
                  <UiKit.Button
                    size="small"
                    type="link"
                    disabled={
                      !outgoingManager.isWasRequest || !outgoingManager.isConfirmationExpired
                    }
                    customStyles={{ disabledColor: `${colorsPalette.gray100}` as Color }}
                    onClick={() => coreManager.openChangePhoneForm()}
                  />
                ),
              }}
            />
          </Description>
        )}
      </Header>
      {coreManager.verificationPrepareStatus === LoadingStatus.LOADING ||
      coreManager.enterPhoneLoading === LoadingStatus.LOADING ? (
        <UiKit.Spin />
      ) : (
        <>
          {method === 'outgoing' && <OutgoingConfirmation />}
          {method === 'incoming' && <IncomingConfirmation />}
          {method === 'qr' && <QRConfirmation />}
        </>
      )}
      <PhoneMethodSelector />
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 40px;
`;
const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;
const Heading = styled.div<{ $isMobile: boolean }>`
  font-family: 'SkorozvonRoboto';
  font-weight: 500;
  font-size: ${({ $isMobile }) => ($isMobile ? '25px' : '38px')};
  line-height: 46px;
  color: ${colorsPalette.gray700};
  text-align: center;
`;
const Description = styled.div<{ $isMobile: boolean }>`
  font-size: ${({ $isMobile }) => ($isMobile ? '12px' : '14px')};
  line-height: 22px;
  color: ${colorsPalette.gray600};
  text-align: center;
`;

PhoneConfirmation = observer(PhoneConfirmation);

export { PhoneConfirmation };
